import { Clock, Inbox, RefreshCw, RotateCcw, Timer } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import { api } from '@/api';
import type { CaseKind, InboxCase } from '@/api/areas/market';
import type { Tone } from '@/components/Badge';
import { Callout, DataTable, Field, FilterBar, Section, StatCard, StatGrid, type Column } from '@/components/blocks';
import { DataError } from '@/components/DataError';
import { PageHeader, useDocumentTitle } from '@/components/PageHeader';
import { EmptyState, Loading } from '@/components/States';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { formatCount } from '@/lib/format';
import { useApi } from '@/lib/useApi';
import { DueCell, EnumBadge, overdueFirst, useEnumLabel, useNow, When, type EnumGroup } from '../../compliance/ui';
import { ABUSE_STATUS, DISCLOSURE_STATUS, DISPUTE_STATUS, PRIORITY } from '../../compliance/vocab';
import { CASE_KIND } from '../shared';

const STATUS: Record<CaseKind, { group: EnumGroup; tones: Readonly<Record<string, { tone: Tone }>> }> = {
  ABUSE: { group: 'abuseStatus', tones: ABUSE_STATUS },
  DISPUTE: { group: 'disputeStatus', tones: DISPUTE_STATUS },
  DISCLOSURE: { group: 'disclosureStatus', tones: DISCLOSURE_STATUS },
};
const SUBTYPE: Record<CaseKind, EnumGroup> = {
  ABUSE: 'abuseCategory',
  DISPUTE: 'disputeType',
  DISCLOSURE: 'requesterType',
};
const KINDS = Object.keys(CASE_KIND) as CaseKind[];
const DAY = 86_400_000;

/** `/compliance/cases`: abuse cases, disputes and disclosure requests in one queue, overdue first. */
export function CaseInboxPage() {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  const label = useEnumLabel();
  const now = useNow();
  useDocumentTitle(t('inbox.title'));
  const [params, setParams] = useSearchParams();
  const kind = (params.get('kind') ?? '') as CaseKind | '';
  const includeClosed = params.get('closed') === '1';
  const domain = params.get('domain') ?? '';
  const result = useApi(
    (signal) =>
      api.getCaseInbox(
        {
          kind: kind || undefined,
          include_closed: includeClosed || undefined,
          domain: domain || undefined,
          limit: 300,
        },
        { signal },
      ),
    [kind, includeClosed, domain],
  );
  const set = (k: string, v: string) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v);
    else p.delete(k);
    setParams(p);
  };
  const items = result.status === 'success' ? overdueFirst(result.data.items, (c) => c.overdue) : [];
  const counts = items.reduce<Record<string, number>>((m, c) => ({ ...m, [c.kind]: (m[c.kind] ?? 0) + 1 }), {});
  const overdue = items.filter((c) => c.overdue).length;
  const soon = items.filter((c) => !c.overdue && c.open && c.due_at && new Date(c.due_at).getTime() - now < DAY).length;
  const filtered = Boolean(kind || includeClosed || domain);

  const columns: Column<InboxCase>[] = [
    {
      key: 'kind',
      header: t('inbox.col.kind'),
      cell: (c) => <EnumBadge group="caseKind" value={c.kind} tones={CASE_KIND} icon={false} />,
    },
    {
      key: 'case',
      header: t('inbox.col.case'),
      cell: (c) => (
        <>
          <Link to={`${CASE_KIND[c.kind].base}/${encodeURIComponent(c.id)}`} className="font-mono text-sm font-semibold text-primary hover:underline">
            {c.reference}
          </Link>
          {c.origin ? <div className="text-xs text-muted-foreground">{c.kind === 'ABUSE' ? label('abuseSource', c.origin) : c.origin}</div> : null}
        </>
      ),
    },
    {
      key: 'domain',
      header: t('inbox.col.domain'),
      cell: (c) =>
        c.domain_id ? (
          <Link to={`/domains/${encodeURIComponent(c.domain_name)}`} className="hover:underline">
            {c.domain_name}
          </Link>
        ) : (
          <span title={t('common.notManaged')}>{c.domain_name}</span>
        ),
    },
    {
      key: 'type',
      header: t('inbox.col.type'),
      cell: (c) => (
        <span className="flex flex-wrap items-center gap-1.5 text-sm">
          <span title={c.subtype}>{label(SUBTYPE[c.kind], c.subtype)}</span>
          {c.priority ? <EnumBadge group="priority" value={c.priority} tones={PRIORITY} /> : null}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('inbox.col.status'),
      cell: (c) => <EnumBadge group={STATUS[c.kind].group} value={c.status} tones={STATUS[c.kind].tones} />,
    },
    {
      key: 'due',
      header: t('inbox.col.due'),
      cell: (c) => <DueCell dueAt={c.open ? c.due_at : null} overdue={c.overdue} policyMissing={c.policy_missing && c.open} />,
    },
    {
      key: 'opened',
      header: t('inbox.col.opened'),
      cell: (c) => <When at={c.opened_at} />,
    },
  ];

  return (
    <>
      <PageHeader
        title={t('inbox.title')}
        crumbs={[{ label: t('crumbs.compliance') }]}
        subtitle={t('inbox.subtitle')}
        actions={
          <Button size="sm" variant="outline" onClick={result.reload}>
            <RefreshCw /> {tc('action.refresh')}
          </Button>
        }
      />
      <div className="space-y-6">
        {result.status === 'success' && result.data.kinds_forbidden.length > 0 ? (
          <Callout tone="info">
            {t('inbox.forbidden', {
              kinds: result.data.kinds_forbidden.map((k) => label('caseKind', k)).join(', '),
            })}
          </Callout>
        ) : null}
        {result.status === 'success' ? (
          <StatGrid className="xl:grid-cols-3">
            <StatCard label={t('inbox.stats.total')} value={formatCount(items.length)} icon={Inbox} />
            <StatCard
              label={t('inbox.stats.overdue')}
              value={formatCount(overdue)}
              tone={overdue > 0 ? 'crit' : 'ok'}
              status={overdue > 0 ? t('sla.overdue') : undefined}
              icon={Clock}
            />
            <StatCard label={t('inbox.stats.dueSoon')} value={formatCount(soon)} tone={soon > 0 ? 'warn' : 'neutral'} icon={Timer} />
          </StatGrid>
        ) : null}
        <Section flush>
          <FilterBar
            className="px-5"
            actions={
              filtered ? (
                <Button variant="ghost" size="sm" onClick={() => setParams(new URLSearchParams())}>
                  <RotateCcw /> {tc('action.reset')}
                </Button>
              ) : null
            }
          >
            <div className="grid gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">{t('inbox.kind')}</span>
              <ToggleGroup
                type="single"
                variant="outline"
                value={kind || 'ALL'}
                onValueChange={(v) => set('kind', v === 'ALL' || !v ? '' : v)}
                aria-label={t('inbox.kind')}
                className="flex-wrap"
              >
                <ToggleGroupItem value="ALL" className="px-3">
                  {t('common.all')}
                </ToggleGroupItem>
                {KINDS.map((k) => (
                  <ToggleGroupItem key={k} value={k} className="px-3">
                    {label('caseKind', k)}
                    {kind === '' && counts[k] ? <span className="text-muted-foreground tabular">{counts[k]}</span> : null}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </div>
            <Field label={t('common.domain')} className="w-full sm:w-56">
              <Input
                key={domain}
                type="search"
                defaultValue={domain}
                onKeyDown={(e) => e.key === 'Enter' && set('domain', e.currentTarget.value.trim())}
                onBlur={(e) => set('domain', e.target.value.trim())}
                placeholder={t('common.exactName')}
              />
            </Field>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control -- the checkbox is the child */}
            <label className="flex h-9 items-center gap-2 text-sm">
              <Checkbox checked={includeClosed} onCheckedChange={(v) => set('closed', v === true ? '1' : '')} />
              {t('inbox.includeClosed')}
            </label>
          </FilterBar>
          {result.status === 'loading' ? (
            <div className="px-5">
              <Loading rows={5} />
            </div>
          ) : null}
          {result.status === 'error' ? (
            <div className="px-5 pb-5">
              <DataError error={result.error} onRetry={result.reload} title={t('inbox.loadError')} />
            </div>
          ) : null}
          {result.status === 'success' ? <DataTable columns={columns} rows={items} rowKey={(c) => `${c.kind}:${c.id}`} empty={<EmptyState title={t('inbox.empty')} />} /> : null}
        </Section>
      </div>
    </>
  );
}
